import { useEffect, useRef } from 'react';

import { AUTO_STATEMENTS_SKIPPED_EVENT, AUTO_STATEMENTS_SKIP_REASON } from './useConnectStatementsData';

import useUpdateOnboardingStep from 'services/bookkeeping/mutations/useUpdateOnboardingStep';
import type { OnboardingProgress } from 'services/bookkeeping/queries/useAccountingOnboardingProgress';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import { isAutoSkippable } from 'modules/Onboarding/AccountingSetup/ConnectStatements/visibility';

interface UseAutoStatementsSkipParams {
    /**
     * Whether this caller is the one that must record the skip. Pass false when the caller's wizard
     * registers the statements step: ConnectStatements records its own skip on arrival, so both
     * firing would write the status twice and double-count the skip funnel. Onboarding progress is
     * global, so routing alone does not stop a wizard writing it.
     */
    enabled: boolean;
    onboardingProgress: OnboardingProgress | undefined;
    /** From useAutoStatementsEligibility; `false` means every business institution is ineligible. */
    eligibility: false | undefined;
}

/**
 * Ineligible banks: persist `skipped` (so the wizard excludes the step) and emit the event we query
 * skips by.
 *
 * Only the accounting wizard uses this, and only while the V1 gate is off — it is the branch that
 * hides the step from an ineligible member, so nobody would otherwise record the skip. Under the
 * gate the step is mandatory and always registered until it is resolved, so the member always
 * reaches ConnectStatements and it writes the skip itself; business accounts setup deliberately
 * does not call this.
 *
 * The reason string here is what the Auto Statements funnel filters on, and it has to stay
 * identical to the one ConnectStatements writes -- which is why the constant is shared rather than
 * spelled out twice.
 */
export default function useAutoStatementsSkip({
    enabled,
    onboardingProgress,
    eligibility,
}: UseAutoStatementsSkipParams): void {
    const updateStep = useUpdateOnboardingStep();
    const { sendTrackingEvent } = useEventTracking();
    const hasAttemptedSkip = useRef(false);

    useEffect(() => {
        if (!enabled || !isAutoSkippable(onboardingProgress, eligibility) || hasAttemptedSkip.current) {
            return;
        }

        hasAttemptedSkip.current = true;
        sendTrackingEvent(AUTO_STATEMENTS_SKIPPED_EVENT, {
            category: 'accounting_setup',
            action: 'auto_statements_skipped',
            skip_reason: AUTO_STATEMENTS_SKIP_REASON,
            stepName: 'connect_statements',
            setup_version: onboardingProgress?.setup_version,
        });
        updateStep.mutate({
            step: 'connect_automated_statements',
            status: 'skipped',
            data: { skip_reason: AUTO_STATEMENTS_SKIP_REASON },
        });
    }, [enabled, onboardingProgress, eligibility, updateStep, sendTrackingEvent]);
}
